import React, { FC } from 'react';
import { FlightFilterModel } from 'ts/flight/classes/FlightFilterModel';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import RadioGroup from 'components/ui/RadioGroup/RadioGroup';
import { formatPriceCurrency } from 'utils/utils';
import { onClickClose, onClickOverlay } from 'utils/responsive';
import FlightResultFilterSlider from './FlightResultFilterSlider';
import FilterResultFilterTime from './FlightResultFilterTime';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { getElapsedStr } from 'utils/flight/flight';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface FlightResultFilterProps {
  filter: FlightFilterModel;
  setFilter: (filter: FlightFilterModel) => void;
  onClickReset: () => void;
}

const FlightResultFilter: FC<FlightResultFilterProps> = ({ filter, setFilter, onClickReset }) => {
  const [t] = useTranslation();
  const onClickCheckbox = (
    value: boolean,
    index: number,
    option: 'stops' | 'airlines' | 'airports' | 'flightTypes' | 'baggage'
  ) => {
    const newStops = [...filter[option]];
    newStops[index].checked = value;
    setFilter({ ...filter, [option]: newStops });
  };

  const onClickBaggageRadio = (value: number) => {
    const newBaggage = filter.baggage.map((baggage) => ({
      ...baggage,
      checked: value === -1 ? false : value === baggage.number,
    }));
    setFilter({ ...filter, baggage: newBaggage });
  };

  const label = (entity: any, tr?: boolean) => {
    return (
      <span className="flex-between w100p text-lh-sm">
        {tr ? t(entity.label) : entity.label}
        <span className="text-sm flight-filter-text">{entity.minPrice}</span>
      </span>
    );
  };

  const selectedBaggage = filter.baggage.find((b) => b.checked);

  const showClearFilter = () => {
    const depTimesChanged =
      filter.departureOutboundTime &&
      filter.departureOutboundTime &&
      filter.arrivalOutboundTime &&
      filter.arrivalOutboundTime &&
      (filter.departureOutboundTime.value[0] !== filter.departureOutboundTime.min ||
        filter.departureOutboundTime.value[1] !== filter.departureOutboundTime.max ||
        filter.arrivalOutboundTime.value[0] !== filter.arrivalOutboundTime.min ||
        filter.arrivalOutboundTime.value[1] !== filter.arrivalOutboundTime.max);

    const arrTimesChanged =
      filter.isInbound &&
      filter.departureInboundTime &&
      (filter.departureInboundTime.value[0] !== filter.departureInboundTime.min ||
        filter.departureInboundTime.value[1] !== filter.departureInboundTime.max) &&
      filter.arrivalInboundTime &&
      (filter.arrivalInboundTime.value[0] !== filter.arrivalInboundTime.min ||
        filter.arrivalInboundTime.value[1] !== filter.arrivalInboundTime.max);
    return (
      filter.stops.some((stop) => stop.checked) ||
      filter.baggage.some((baggage) => baggage.checked) ||
      filter.airlines.some((airline) => airline.checked) ||
      filter.airports.some((airport) => airport.checked) ||
      filter.flightTypes.some((flightType) => flightType.checked) ||
      filter.price.value[0] !== filter.price.min ||
      filter.price.value[1] !== filter.price.max ||
      filter.duration.value[0] !== filter.duration.min ||
      filter.duration.value[1] !== filter.duration.max ||
      filter.stopDuration.value[0] !== filter.stopDuration.min ||
      filter.stopDuration.value[1] !== filter.stopDuration.max ||
      depTimesChanged ||
      arrTimesChanged
    );
  };

  return (
    <div className="flight-filter-wr default-shadow" onClick={onClickOverlay}>
      <div className="flight-filter">
        <div className="results-responsive-header results-responsive-header-flight">
          <span className="title text-md">Filter</span>
          <FontAwesomeIcon icon={faTimes as any} onClick={onClickClose} />
        </div>
        <section className="flight-filter-section" data-assistant-target="stops">
          <div className="flex-between mb-15">
            <div className="text-bold">{t('filter.stops')}</div>
            {showClearFilter() && (
              <div className="text-underline text-sm color-danger" onClick={onClickReset}>
                {t('filter.reset')}
              </div>
            )}
          </div>

          {filter.stops.map((stop, index) => (
            <Checkbox
              key={stop.number}
              checked={stop.checked}
              htmlLabel={label(stop, true)}
              onChange={(value) => onClickCheckbox(value, index, 'stops')}
              className="mb-10 text-sm"
              disabled={stop.disabled}
            />
          ))}
        </section>

        <section className="flight-filter-section" data-assistant-target="baggage">
          <div className="text-bold mb-15">{t('filter.baggage')}</div>
          <RadioGroup
            className="text-sm"
            value={selectedBaggage ? String(selectedBaggage.number) : '-1'}
            onChange={(value) => onClickBaggageRadio(Number(value))}
            options={[
              ...filter.baggage,
              {
                number: -1,
                label: 'all',
                minPrice: '',
                checked: !filter.baggage.some((b) => b.checked),
              },
            ]
              .reverse()
              .map((baggage) => ({
                value: String(baggage.number),
                label: baggage.label,
                jsxLabel: label(baggage, true),
              }))}
            needToTranslate
          />
        </section>
        <section className="flight-filter-section" data-assistant-target="price">
          <div className="text-bold mb-15">{t('filter.price_range')}</div>
          <FlightResultFilterSlider
            filter={filter}
            setFilter={setFilter}
            minStr={formatPriceCurrency(filter.price.value[0], filter.price.currencySign)}
            maxStr={formatPriceCurrency(filter.price.value[1], filter.price.currencySign)}
            type="price"
          />
        </section>

        {!filter.isMulti && (
          <div data-assistant-target="times">
            <FilterResultFilterTime filter={filter} setFilter={setFilter} />
          </div>
        )}

        <section className="flight-filter-section" data-assistant-target="duration">
          <div className="text-bold mb-15">{t('filter.duration')}</div>
          <FlightResultFilterSlider
            filter={filter}
            setFilter={setFilter}
            minStr={getElapsedStr(t, filter.duration.value[0])}
            maxStr={getElapsedStr(t, filter.duration.value[1])}
            type="duration"
          />
        </section>

        <section className="flight-filter-section" data-assistant-target="duration">
          <div className="text-bold mb-15">{t('filter.stop_duration')}</div>
          <FlightResultFilterSlider
            filter={filter}
            setFilter={setFilter}
            minStr={getElapsedStr(t, filter.stopDuration.value[0])}
            maxStr={getElapsedStr(t, filter.stopDuration.value[1])}
            type="stopDuration"
          />
        </section>

        <section className="flight-filter-section" data-assistant-target="airlines">
          <div className="text-bold mb-15">{t('filter.airlines')}</div>
          {filter.airlines.map((airline, index) => (
            <Checkbox
              key={airline.code}
              htmlLabel={label({ ...airline, label: airline.label || airline.code })}
              checked={airline.checked}
              onChange={(value) => onClickCheckbox(value, index, 'airlines')}
              className="mb-10 text-sm"
            />
          ))}
        </section>

        <section className="flight-filter-section" data-assistant-target="airports">
          <div className="text-bold mb-15">{t('filter.airports')}</div>
          {filter.airports.map((airport, index) => (
            <Checkbox
              key={airport.code}
              htmlLabel={label(airport)}
              checked={airport.checked}
              onChange={(value) => onClickCheckbox(value, index, 'airports')}
              className="mb-10 text-sm"
            />
          ))}
        </section>

        <section className="flight-filter-section" data-assistant-target="flightTypes">
          <div className="text-bold mb-15">{t('flight.s')}</div>
          {filter.flightTypes.map((type, index) => (
            <Checkbox
              key={type.code}
              htmlLabel={label({ ...type, label: type.label || 'שכר' }, true)}
              checked={type.checked}
              onChange={(value) => onClickCheckbox(value, index, 'flightTypes')}
              className="mb-10 text-sm"
            />
          ))}
        </section>
      </div>
    </div>
  );
};

export default withErrorBoundary(FlightResultFilter, 'FlightResultFilter');
